'use client';

import React, { useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';

import { HeaderText } from '@/components/ui';
import { HeaderSize } from '@/components/ui/HeaderText';

import {
  DRAWER_EVENT_TYPES,
  DrawerEventData,
  track,
} from '@/utils/rudderstack';

type DrawerProps = {
  open: boolean;
  onOpen?: () => void;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  widthClassName?: string;
  eventData?: DrawerEventData;
  customClassNames?: string;
};

export default function Drawer({
  open,
  onOpen,
  onClose,
  title = '',
  children,
  widthClassName,
  eventData,
  customClassNames,
}: DrawerProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);

  const handleOpen = useCallback(() => {
    track(DRAWER_EVENT_TYPES.DRAWER_VIEWED, {
      url: window.location.href,
      ...eventData,
    });

    onOpen?.();
  }, [eventData, onOpen]);

  const handleClose = () => {
    track(DRAWER_EVENT_TYPES.DRAWER_CLOSED, {
      url: window.location.href,
      ...eventData,
    });

    onClose();
  };

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }

    document.addEventListener('keydown', onKey);

    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (open) {
      const prevOverflow = document.body.style.overflow;

      document.body.style.overflow = 'hidden';

      const id = requestAnimationFrame(() => panelRef.current?.focus());

      handleOpen();

      return () => {
        cancelAnimationFrame(id);

        document.body.style.overflow = prevOverflow;
      };
    }
  }, [open, handleOpen]);

  return (
    <>
      <div
        className={classNames(
          'fixed inset-0 z-[99999] bg-black/65 transition-opacity duration-300',
          {
            'opacity-100': open,
            'opacity-0 pointer-events-none': !open,
          },
        )}
        onClick={handleClose}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'drawer-title' : undefined}
        tabIndex={open ? 0 : -1}
        ref={panelRef}
        className={classNames(
          'fixed right-0 top-0 z-[99999] h-dvh bg-white shadow-2xl',
          'transition-transform duration-300 will-change-transform',
          'flex flex-col',
          widthClassName || 'md:w-[min(90vw,640px)] w-full',
          {
            'translate-x-0': open,
            'translate-x-full': !open,
          },
        )}
      >
        <div className="sticky top-0 flex items-center justify-between gap-2 border-b border-grey-mid-light px-6 md:px-8 py-4 md:py-6">
          <HeaderText
            text={title}
            align="left"
            size={HeaderSize.H3}
            classes=""
            data-testid="account-page-header-text"
          />

          <button onClick={handleClose} aria-label="Close drawer">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="h-8 w-8 text-grey-dark"
            >
              <path
                fillRule="evenodd"
                d="M6.225 4.811a.75.75 0 011.06 0L12 9.525l4.715-4.714a.75.75 0 111.06 1.06L13.06 10.586l4.715 4.714a.75.75 0 11-1.06 1.06L12 11.646l-4.715 4.714a.75.75 0 11-1.06-1.06l4.714-4.715-4.714-4.714a.75.75 0 010-1.06z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        <div
          className={classNames(
            'min-h-0 flex-1 overflow-y-auto p-4',
            customClassNames,
          )}
        >
          {children}
        </div>
      </div>
    </>
  );
}
